/**
 * "A child owns THIS dismiss gesture."
 *
 * An overlay that can be dismissed on its own — an open picker list, a
 * calendar, an inline row editor — marks itself `data-escape-owner="true"`.
 * The Escape path has read that marker for a while (`Modal.tsx`,
 * `PSeatCellEditor`, `HubDialogShell`): the first Escape closes the child, not
 * the host behind it.
 *
 * The PRESS path needs a rule of the same shape, and only got one once
 * `OptionInput` stopped covering the viewport with a click shield while its
 * list was open. That shield was swallowing the first click on everything
 * else in the dialog — including Save — which is why it had to go. But it was
 * also, by accident, what stopped a backdrop press from reaching the host:
 * without it, the press that closes an open list ALSO closes the modal behind
 * it, which for some hosts means discarding a condition tree, raising a
 * "discard changes?" prompt or eating a half-typed name. One click, two
 * dismissals, one of them unasked.
 *
 * So the host asks, at press time, whether a child owned the gesture.
 *
 * A SEPARATE MARKER from the Escape one, deliberately: `data-press-owner`.
 * The Escape marker is also carried by things a press does NOT dismiss — an
 * expanded inline row editor (`OfferListEditor`), a whole payment dialog
 * (`CreateEventPage`), an open calendar in a table cell — and reading it on
 * the press path made a modal's backdrop a silent no-op for as long as any of
 * those was open (the audit of the first version found exactly that in the
 * programme editor). Only an overlay that the press itself will close — today:
 * an OptionInput list, which closes on the blur the press causes — sets the
 * press marker, and only while it is actually visible.
 *
 * TIMING IS LOAD BEARING: read this while the press is still DOWN
 * (`mousedown` / capture-phase `pointerdown`). By `click` the input has
 * blurred, the list has closed and the marker is already gone.
 *
 * SCOPE IS LOAD BEARING TOO: pass the host's own subtree where there is one.
 * Asked of the whole document, a list open in some other part of the page
 * would answer yes and cost the host one extra press to close.
 */
export const PRESS_OWNER_ATTR = "data-press-owner";

export const hasDismissOwner = (root?: ParentNode | null): boolean => {
  const scope = root ?? (typeof document === "undefined" ? null : document);
  return !!scope?.querySelector(`[${PRESS_OWNER_ATTR}="true"]`);
};
